import FilterListIcon from "@mui/icons-material/FilterList";
import {
  Alert,
  Box,
  IconButton,
  Link,
  Paper,
  Skeleton,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  useTheme,
} from "@mui/material";
import React from "react";

import { DevicesTableFilterChips } from "@/Device/Devices/DevicesTableFilterChips.tsx";
import { RobotoTableCell, RobotoTableRow } from "@/shared/components";
import { DeviceLink } from "@/shared/components/DeviceLink";
import { RobotoPrincipalUnderlineLink } from "@/shared/components/principal/RobotoPrincipalUnderlineLink";
import type { Device } from "@/shared/domain/devices";
import { useDevicesByOrg } from "@/shared/domain/devices/hooks/useDevicesByOrg.ts";
import { usePopupState } from "@/shared/hooks/usePopupState";

import styles from "./DevicesTable.module.css";
import { DevicesTableFilterPopover } from "./DevicesTableFilterPopover";
import { DevicesTableFilter } from "./types";

type RowsPerPage = 10 | 25 | 50;
const INITIAL_ROWS_PER_PAGE = 25;
// We want to make sure that we continue to fetch results until we have at least one max-page's worth of results, or
// we've gotten all results and only found less than a page's worth of results.
const AUTO_FETCH_MORE_RESULTS_THRESHOLD = 50;

interface DevicesTableProps {
  orgId: string | undefined;
}

interface LoadingTableSkeletonProps {
  show: boolean;
}

interface NoResultsMessageProps {
  show: boolean;
}

const NoResultsMessage: React.FC<NoResultsMessageProps> = ({ show }) => {
  const theme = useTheme();

  if (!show) {
    return null;
  }

  return (
    <RobotoTableRow className={styles.tableRow}>
      <RobotoTableCell
        colSpan={3}
        align="center"
        style={{
          paddingBottom: theme.spacing(2),
          paddingTop: theme.spacing(2),
        }}
      >
        <Alert severity="warning">
          No devices found. Refer to the docs to{" "}
          <Link
            underline="hover"
            target="_blank"
            href="https://docs.roboto.ai/learn/devices.html"
          >
            register
          </Link>{" "}
          a new device.
        </Alert>
      </RobotoTableCell>
    </RobotoTableRow>
  );
};

const LoadingTableSkeleton: React.FC<LoadingTableSkeletonProps> = ({
  show,
}) => {
  if (!show) {
    return null;
  }
  return (
    <>
      {Array.from({ length: 3 }, (_, i) => (
        <RobotoTableRow key={`skeleton-row-${i}`} className={styles.tableRow}>
          {Array.from({ length: 3 }, (_, j) => (
            <RobotoTableCell key={`skeleton-cell-${j}`}>
              <Skeleton
                animation="wave"
                variant="rounded"
                sx={{ mt: 1, mb: 1 }}
              />
            </RobotoTableCell>
          ))}
        </RobotoTableRow>
      ))}
    </>
  );
};

interface FilterableColumnHeaderProps {
  title: string;
  popup: ReturnType<typeof usePopupState<void>>;
  tooltipText: string;
}

const FilterableColumnHeader: React.FC<FilterableColumnHeaderProps> = ({
  title,
  popup,
  tooltipText,
}) => {
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
      <IconButton
        size="small"
        onClick={(event) => {
          popup.open({ anchorEl: event.currentTarget });
        }}
        title={tooltipText}
      >
        <FilterListIcon fontSize="small" />
      </IconButton>
      {title}
    </Box>
  );
};

interface DeviceRowProps {
  device: Device;
}

const DeviceRow: React.FC<DeviceRowProps> = ({ device }) => {
  return (
    <RobotoTableRow className={styles.tableRow}>
      <RobotoTableCell>
        <DeviceLink deviceId={device.device_id} />
      </RobotoTableCell>
      <RobotoTableCell>
        <RobotoPrincipalUnderlineLink principal={device.created_by} icon />
      </RobotoTableCell>
      <RobotoTableCell>
        {new Date(device.created).toLocaleString()}
      </RobotoTableCell>
    </RobotoTableRow>
  );
};

export const DevicesTable: React.FC<DevicesTableProps> = ({ orgId }) => {
  const {
    data: devices = [],
    isLoading,
    error,
    hasNextPage,
    fetchNextPage,
  } = useDevicesByOrg(orgId);

  const theme = useTheme();

  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState<RowsPerPage>(
    INITIAL_ROWS_PER_PAGE,
  );

  // Filter states
  const [filter, setFilter] = React.useState<DevicesTableFilter>({
    deviceId: null,
    createdBy: null,
  });

  // Popup states for column filters
  const deviceIdFilterPopup = usePopupState();
  const createdByFilterPopup = usePopupState();

  const hasActiveFilters =
    filter.deviceId !== null || filter.createdBy !== null;

  const filteredDevices = React.useMemo(() => {
    return devices.filter((device) => {
      const matchesDeviceId =
        filter.deviceId === null ||
        device.device_id.toLowerCase().includes(filter.deviceId.toLowerCase());
      const matchesCreatedBy =
        filter.createdBy === null ||
        device.created_by
          .toLowerCase()
          .includes(filter.createdBy.toLowerCase());

      return matchesDeviceId && matchesCreatedBy;
    });
  }, [devices, filter]);

  // Auto-fetch more data when filtering if we have few results and more data is available
  React.useEffect(() => {
    const shouldFetchMore =
      hasActiveFilters &&
      filteredDevices.length < AUTO_FETCH_MORE_RESULTS_THRESHOLD &&
      hasNextPage &&
      !isLoading;

    if (shouldFetchMore) {
      void fetchNextPage();
    }
  }, [
    hasActiveFilters,
    filteredDevices.length,
    hasNextPage,
    isLoading,
    fetchNextPage,
  ]);

  // Calculate pagination on filtered data
  const startIndex = page * rowsPerPage;
  const endIndex = startIndex + rowsPerPage;
  const pageData = filteredDevices.slice(startIndex, endIndex);

  // Handle pagination changes
  const handlePageChange = React.useCallback(
    async (newPage: number) => {
      setPage(newPage);

      if (hasActiveFilters) {
        // For filtered data, fetch more if we're approaching the end of filtered results
        const filteredItemsNeeded = (newPage + 1) * rowsPerPage;
        if (filteredItemsNeeded > filteredDevices.length - 10 && hasNextPage) {
          await fetchNextPage();
        }
      } else {
        // For unfiltered data, fetch more if we're approaching the end of loaded data
        const totalLoadedItems = devices.length;
        const itemsNeeded = (newPage + 1) * rowsPerPage;

        if (itemsNeeded > totalLoadedItems - 10 && hasNextPage) {
          await fetchNextPage();
        }
      }
    },
    [
      devices.length,
      filteredDevices.length,
      rowsPerPage,
      hasNextPage,
      fetchNextPage,
      hasActiveFilters,
    ],
  );

  const handleRowsPerPageChange = React.useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const newRowsPerPage = parseInt(event.target.value, 10) as RowsPerPage;
      setRowsPerPage(newRowsPerPage);
      setPage(0);
    },
    [],
  );

  // Reset page when filters change and preemptively fetch more data when filters are cleared
  React.useEffect(() => {
    setPage(0);

    // If filters were just cleared and we don't have much data, fetch more
    if (!hasActiveFilters && devices.length < 50 && hasNextPage) {
      void fetchNextPage();
    }
  }, [filter, hasActiveFilters, devices.length, hasNextPage, fetchNextPage]);

  // Calculate count for pagination
  let paginationCount: number;
  if (hasNextPage) {
    paginationCount = -1;
  } else if (hasActiveFilters) {
    paginationCount = filteredDevices.length;
  } else {
    paginationCount = devices.length;
  }

  let disableNextButton: boolean;
  if (hasActiveFilters) {
    disableNextButton = !hasNextPage && endIndex >= filteredDevices.length;
  } else {
    disableNextButton = !hasNextPage && endIndex >= devices.length;
  }

  return (
    <Box>
      {error && (
        <Alert severity="error">
          Failed to fetch devices. Please contact{" "}
          <a href="mailto:support@roboto.ai">support@roboto.ai</a> if this
          problem persists.
        </Alert>
      )}

      {hasActiveFilters && (
        <Box sx={{ mb: theme.spacing(2) }}>
          <DevicesTableFilterChips filter={filter} setFilter={setFilter} />
        </Box>
      )}

      <TableContainer component={Paper} variant="outlined">
        <Table size="small" sx={{ tableLayout: "fixed", width: "100%" }}>
          <TableHead>
            <TableRow className={styles.tableRow}>
              <RobotoTableCell>
                <FilterableColumnHeader
                  title="Device ID"
                  popup={deviceIdFilterPopup}
                  tooltipText="Filter Device ID"
                />
              </RobotoTableCell>
              <RobotoTableCell>
                <FilterableColumnHeader
                  title="Created By"
                  popup={createdByFilterPopup}
                  tooltipText="Filter Created By"
                />
              </RobotoTableCell>
              <RobotoTableCell>Created</RobotoTableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <LoadingTableSkeleton show={isLoading} />
            <NoResultsMessage show={pageData.length === 0 && !isLoading} />
            {pageData.map((device) => (
              <DeviceRow key={device.device_id} device={device} />
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <DevicesTableFilterPopover
        popup={deviceIdFilterPopup}
        filter={filter.deviceId || ""}
        onFilterApply={(value) => {
          setFilter({ ...filter, deviceId: value || null });
        }}
        placeholder="Filter by Device ID"
      />

      <DevicesTableFilterPopover
        popup={createdByFilterPopup}
        filter={filter.createdBy || ""}
        onFilterApply={(value) => {
          setFilter({ ...filter, createdBy: value || null });
        }}
        placeholder="Filter by Created By"
      />

      <TablePagination
        component="div"
        count={paginationCount}
        slotProps={{
          actions: {
            nextButton: {
              disabled: disableNextButton,
            },
          },
        }}
        rowsPerPageOptions={[10, 25, 50]}
        page={page}
        onPageChange={(_, newPage) => {
          void handlePageChange(newPage);
        }}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={handleRowsPerPageChange}
        labelRowsPerPage={"Devices per page:"}
      />
    </Box>
  );
};
